/**
 * loginContextChoiceStore — lightweight pub/sub for surfacing the
 * LoginContextChoiceModal from anywhere in the app, without depending on
 * the lifecycle of the component that triggered the sign-in.
 *
 * Why: LiteAuthSheet is mounted with `{!user && <LiteAuthSheet/>}`. The
 * moment a sign-in succeeds, `user` flips truthy and the sheet (along with
 * any modal it owned) unmounts before the modal can render. By raising a
 * window event and rendering a global host at the storefront/app root, the
 * choice modal survives the auth flow.
 */
import { useEffect, useState } from "react";

const EVT = "khays:request-login-context-choice";

export interface LoginContextRequest {
  userId: string;
  role: string | null;
}

export function requestLoginContextChoice(req: LoginContextRequest) {
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: req }));
  } catch {}
}

export function useLoginContextChoiceRequest(): {
  req: LoginContextRequest | null;
  clear: () => void;
} {
  const [req, setReq] = useState<LoginContextRequest | null>(null);
  useEffect(() => {
    const onEvt = (e: Event) => {
      const detail = (e as CustomEvent).detail as LoginContextRequest;
      if (detail?.userId) setReq(detail);
    };
    window.addEventListener(EVT, onEvt);
    return () => window.removeEventListener(EVT, onEvt);
  }, []);
  return { req, clear: () => setReq(null) };
}
